import {
  color,
  Flex,
  Link,
  PrimaryText,
  spacing,
  StyleProps,
  typography,
  useColorMode,
} from 'deepstash-ui';
import React from 'react';
import { UserData } from 'types';
import { onLinkClick, SQUARE_WIDTH_REM } from 'utils/global';
import FollowUserButtonV2 from '../buttons/FollowUserButtonV2';
import BaseCard from '../cards/BaseCard';
import NextLink from '../navigation/NextLink';
import ProfilePicture from './ProfilePicture';

interface ProfileCardProps extends StyleProps {
  user: UserData;
  size: 'sm' | 'lg';
  withBackground?: boolean;
  maxNoOfLinesBio?: number;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  user,
  size,
  withBackground,
  maxNoOfLinesBio,
  ...props
}) => {
  const { colorMode } = useColorMode();

  const renderProfilePicture = () => (
    <NextLink href={`/u/${user.username}`} passHref>
      <Link
        _hover={undefined}
        _active={undefined}
        _focus={undefined}
        onClick={onLinkClick}
      >
        <ProfilePicture
          boxSize="4rem"
          photo={user.photo ?? ''}
          size="xl"
          altTag={user.username}
          imageBlurhash={user.photo_blurhash}
          p="0"
          pr={spacing.M.rem}
          mb={spacing.S.rem}
        />
      </Link>
    </NextLink>
  );

  const userInfo = (
    <NextLink href={`/u/${user.username}`} passHref>
      <Link
        _hover={undefined}
        _active={undefined}
        _focus={undefined}
        onClick={onLinkClick}
      >
        <PrimaryText
          color={color[colorMode].text}
          {...typography.primaryHeadingFonts.small.bold}
        >
          {user.firstName + ' ' + user.lastName}
        </PrimaryText>
        <PrimaryText
          mb={spacing.S.rem}
          color={color[colorMode].text}
          opacity={0.4}
          {...typography.primaryTextFonts.small}
        >
          {'@' + user.username}
        </PrimaryText>
      </Link>
    </NextLink>
  );

  const userBio = (
    <PrimaryText
      color={color[colorMode].textSecondary}
      noOfLines={maxNoOfLinesBio}
      {...typography.primaryTextFonts.small}
    >
      {user.bio}
    </PrimaryText>
  );

  if (size === 'lg') {
    return (
      <BaseCard
        width="100%"
        flexDirection="column"
        bgColor={withBackground ? color[colorMode].surface : 'unset'}
        hasHoverEffect={!!withBackground}
        py={spacing.XS.rem}
        {...props}
      >
        {renderProfilePicture()}
        <Flex justifyContent="space-between">
          <Flex flexDirection="column">{userInfo}</Flex>
          <FollowUserButtonV2
            userId={user.id}
            height={SQUARE_WIDTH_REM}
            width="8rem"
          />
        </Flex>
        {userBio}
      </BaseCard>
    );
  } else {
    return (
      <BaseCard
        width="100%"
        bgColor={withBackground ? color[colorMode].surface : 'unset'}
        hasHoverEffect={!!withBackground}
        {...props}
      >
        {renderProfilePicture()}
        <Flex flexDir="column">
          {userInfo}
          {userBio}
        </Flex>
      </BaseCard>
    );
  }
};

export default ProfileCard;
